/**
 * /onboarding — wizard de aceptación post-signup.
 *
 * Carga HTML de Términos + Mandato vigente, muestra con scroll, exige
 * checkbox por cada uno antes de POST /admin/onboarding/accept.
 *
 * Usa onboardingApi.status() para skipear pasos ya completados.
 */

import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, FileText } from 'lucide-react'
import { toast } from 'sonner'

import { AppShell } from '@/components/layout/app-shell'
import { onboardingApi, type ContractKind } from '@/lib/api'
import { ROUTES } from '@/lib/constants'

export function OnboardingPage() {
  const nav = useNavigate()
  const status = useQuery({ queryKey: ['onboarding-status'], queryFn: onboardingApi.status })

  // Determinar qué contratos faltan.
  const pending: ContractKind[] = []
  if (status.data) {
    if (!status.data.terms_up_to_date) pending.push('terms')
    if (!status.data.mandate_up_to_date) pending.push('mandate')
  }

  // Si todo está al día, redirigir al dashboard.
  useEffect(() => {
    if (status.data && pending.length === 0) {
      toast.success('Onboarding completo')
      nav(ROUTES.DASHBOARD)
    }
  }, [status.data, pending.length, nav])

  if (status.isLoading) {
    return (
      <AppShell>
        <p className="text-sm text-fg-muted">Cargando estado...</p>
      </AppShell>
    )
  }

  return (
    <AppShell>
      <div className="mx-auto max-w-3xl">
        <h1 className="text-3xl font-bold">Aceptación de contratos</h1>
        <p className="mt-2 text-sm text-fg-secondary">
          Antes de emitir documentos debes aceptar los siguientes contratos.
          Cada aceptación queda registrada con audit court-ready (hash chain
          + IP + User-Agent + timestamp UTC).
        </p>

        <div className="mt-6 space-y-6">
          {pending.includes('terms') && <ContractCard kind="terms" />}
          {pending.includes('mandate') && <ContractCard kind="mandate" />}
        </div>

        <div className="mt-8 text-center">
          <Link to={ROUTES.DASHBOARD} className="text-sm text-fg-muted hover:text-fg-primary">
            ← Volver al dashboard
          </Link>
        </div>
      </div>
    </AppShell>
  )
}

function ContractCard({ kind }: { kind: ContractKind }) {
  const queryClient = useQueryClient()
  const contract = useQuery({
    queryKey: ['contract', kind],
    queryFn: () => onboardingApi.getContract(kind),
  })
  const [acked, setAcked] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const handleAccept = async () => {
    setSubmitting(true)
    try {
      const res = await onboardingApi.acceptContract(kind)
      toast.success(`Aceptado ${kind === 'terms' ? 'Términos' : 'Mandato'} v${res.contract_version}`)
      // Invalidar el status: la page padre recalcula `pending` y su
      // useEffect navega al dashboard cuando todo queda al día.
      await queryClient.invalidateQueries({ queryKey: ['onboarding-status'] })
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } }; message?: string }
      toast.error(`No se pudo aceptar: ${e?.response?.data?.message ?? e?.message ?? 'error desconocido'}`)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="rounded-xl border border-border bg-bg-surface p-6">
      <div className="mb-4 flex items-center gap-2">
        <FileText className="h-5 w-5 text-brand-600" />
        <h2 className="text-lg font-semibold">
          {kind === 'terms' ? 'Términos y Condiciones' : 'Contrato de Mandato DIAN'}
          {contract.data && (
            <span className="ml-2 text-xs font-mono text-fg-muted">{contract.data.version}</span>
          )}
        </h2>
      </div>

      {contract.isLoading && <p className="text-sm text-fg-muted">Cargando contrato...</p>}
      {contract.data && (
        <iframe
          srcDoc={contract.data.html}
          title={`Contrato ${kind}`}
          className="h-96 w-full rounded-md border border-border bg-white"
        />
      )}

      <label className="mt-4 flex items-start gap-2 text-sm">
        <input type="checkbox" checked={acked} onChange={(e) => setAcked(e.target.checked)} className="mt-0.5" />
        <span>
          He leído, comprendido y acepto el contenido del contrato anterior. Mi
          aceptación queda registrada con audit forensic completo (IP, User-Agent,
          timestamp, hash criptográfico).
        </span>
      </label>

      <button
        type="button"
        onClick={handleAccept}
        disabled={!acked || submitting}
        className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
      >
        <Check className="h-4 w-4" />
        {submitting ? 'Registrando...' : `Aceptar ${kind === 'terms' ? 'Términos' : 'Mandato'}`}
      </button>
    </div>
  )
}
